import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * The game the trader currently has open, as the Game Detail page resolved it
 * from the live feed.
 *
 * Exists because a `/game/...` URL is a weaker identity than the page's own
 * header: several surfaces still mint the legacy sport-less link (My Trades,
 * Live Discovery, Position Tracker's Poly tables, GameTable's fallback), and
 * one of them used to alpha-sort the codes. Reading the resolved row instead
 * means the tab title says exactly what the header says, no matter which
 * surface opened the game (#781).
 *
 * Published by the Game Detail page for as long as it is mounted; null the
 * rest of the time. Consumers must still check the identity matches the route
 * they are titling — during a navigation between two games there is a frame
 * where the URL has changed and the row has not.
 */
export interface OpenGameIdentity {
  /** Display codes in game-day order, as the header prints them. */
  awayCode: string;
  homeCode: string;
  /** g1 sport id (lowercase), or null when the row carries none. */
  sport: string | null;
}

interface OpenGameState {
  game: OpenGameIdentity | null;
  setGame: (game: OpenGameIdentity | null) => void;
}

export const useOpenGameStore = create<OpenGameState>((set) => ({
  game: null,
  setGame: (game) => set({ game }),
}));

/**
 * Publish the resolved game for as long as the caller is mounted, clearing it
 * on unmount. Pass null while the row hasn't resolved (an archived game whose
 * live frames hydra has pruned never resolves at all) — consumers fall back to
 * the URL.
 */
export function usePublishOpenGame(game: OpenGameIdentity | null): void {
  const awayCode = game?.awayCode ?? null;
  const homeCode = game?.homeCode ?? null;
  const sport = game?.sport ?? null;
  useEffect(() => {
    const setGame = useOpenGameStore.getState().setGame;
    setGame(awayCode && homeCode ? { awayCode, homeCode, sport } : null);
    return () => setGame(null);
  }, [awayCode, homeCode, sport]);
}
